/**
 * AzureIdentityChip — header chip showing the active discovery identity
 * (source + account) with a dropdown to switch identity. Both identities are
 * always listed; the Azure CLI entry is disabled with a hint when `az` is not
 * detected. When Entra is the active identity the menu also offers
 * "Sign in with a different account…" and "Sign out". Switching / signing
 * actions are delegated to the parent, which owns analytics + refetch.
 */
import { Check, ChevronDown, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type {
  AzureIdentityInfo,
  AzureIdentitySource,
} from '../../../types/azureDiscovery';

const SOURCE_LABEL: Record<AzureIdentitySource, string> = {
  'az-cli': 'Azure CLI',
  entra: 'Microsoft Entra ID',
};

interface AzureIdentityChipProps {
  identity?: AzureIdentityInfo;
  activeSource?: AzureIdentitySource;
  onSwitch: (source: AzureIdentitySource) => void;
  onSignInDifferent: () => void;
  onSignOut: () => void;
}

export function AzureIdentityChip({
  identity,
  activeSource,
  onSwitch,
  onSignInDifferent,
  onSignOut,
}: AzureIdentityChipProps) {
  const available = identity?.available ?? { azCli: true, entra: true };
  const effective = activeSource ?? identity?.source;
  const entraActive = effective === 'entra';
  const label = effective ? SOURCE_LABEL[effective] : 'Not signed in';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 px-2 text-xs"
          data-testid="azure-picker-identity-chip"
        >
          <UserRound className="h-3.5 w-3.5 opacity-70" aria-hidden="true" />
          <span className="max-w-[220px] truncate">
            {label}
            {identity?.account ? <span className="text-muted-foreground"> · {identity.account}</span> : null}
          </span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          Discovery identity
        </DropdownMenuLabel>
        <IdentityOption
          source="az-cli"
          active={effective === 'az-cli'}
          disabled={!available.azCli}
          hint={!available.azCli ? 'Run “az login” to enable' : undefined}
          onSelect={() => onSwitch('az-cli')}
        />
        <IdentityOption
          source="entra"
          active={effective === 'entra'}
          onSelect={() => onSwitch('entra')}
        />
        {entraActive ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onSignInDifferent}>
              Sign in with a different account…
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onSignOut}>Sign out</DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function IdentityOption({
  source,
  active,
  disabled,
  hint,
  onSelect,
}: {
  source: AzureIdentitySource;
  active: boolean;
  disabled?: boolean;
  hint?: string;
  onSelect: () => void;
}) {
  return (
    <DropdownMenuItem
      disabled={disabled}
      onSelect={onSelect}
      className="flex items-start gap-2"
      data-testid={`azure-picker-identity-${source}`}
    >
      <Check className={`mt-0.5 h-4 w-4 shrink-0 ${active ? 'opacity-100' : 'opacity-0'}`} aria-hidden="true" />
      <span className="flex flex-col">
        <span>{SOURCE_LABEL[source]}</span>
        {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
      </span>
    </DropdownMenuItem>
  );
}
